/**
 * PI License Tab Component
 * Displays PI License information and images
 */

import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Grid,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import type { GhostApplicationDetail } from '../../../../../types/ghostVerification';

interface PILicenseTabProps {
  application: GhostApplicationDetail;
}

export const PILicenseTab: React.FC<PILicenseTabProps> = ({ application }) => {
  const theme = useTheme();
  const handleViewDocument = () => {
    if (application.piLicenseInfo?.licenseDocument?.url) {
      window.open(application.piLicenseInfo.licenseDocument.url, '_blank');
    }
  };

  const handleDownloadDocument = () => {
    if (application.piLicenseInfo?.licenseDocument?.url) {
      const link = document.createElement('a');
      link.href = application.piLicenseInfo.licenseDocument.url;
      link.download = application.piLicenseInfo.licenseDocument.name;
      link.click();
    }
  };


  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
        {/* License Details Card */}
        <Grid container spacing={3}>
          {/* Issuing State */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Issuing State
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {application.piLicenseInfo?.issuingStateFull || application.piLicenseInfo?.issuingState || 'N/A'}
            </Typography>
          </Grid>

          {/* License Number */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              License Number
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              {application.piLicenseInfo?.licenseNumber || 'N/A'}
            </Typography>
          </Grid>

          {/* Expiration Date */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Expiration Date
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: application.identityInfo?.idExpiryDate &&
                  new Date(application.identityInfo.idExpiryDate) < new Date()
                  ? theme.palette.error.main
                  : theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: application.identityInfo?.idExpiryDate &&
                  new Date(application.identityInfo.idExpiryDate) < new Date()
                  ? 600
                  : 400,
              }}
            >
              {application.piLicenseInfo?.licenseExpiration
                ? application.piLicenseInfo.licenseExpiration
                : 'N/A'}
              {application.identityInfo?.idExpiryDate &&
                new Date(application.identityInfo.idExpiryDate) < new Date() && (
                  <Chip
                    label="Expired"
                    size="small"
                    sx={{
                      marginLeft: theme.spacing(1),
                      backgroundColor: theme.palette.error.main,
                      color: theme.palette.error.contrastText,
                      fontSize: theme.typography.caption.fontSize,
                      height: 20,
                    }}
                  />
                )}
            </Typography>
          </Grid>

          {/* Years of Experience */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                marginBottom: theme.spacing(1),
                color: theme.palette.text.secondary,
                fontSize: theme.typography.body2.fontSize,
              }}
            >
              Years of Experience
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: theme.palette.text.primary,
                fontSize: theme.typography.body2.fontSize,
                fontWeight: 500,
              }}
            >
              {application.piLicenseInfo?.yearsOfExperience
                ? `${application.piLicenseInfo.yearsOfExperience} years`
                : 'N/A'}
            </Typography>
          </Grid>

          {/* Skills */}
          {application.piLicenseInfo?.skills && application.piLicenseInfo.skills.length > 0 && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                Skills
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(1) }}>
                {application.piLicenseInfo.skills.map((skill, index) => (
                  <Chip
                    key={index}
                    label={skill}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.primary.main,
                      color: theme.palette.primary.contrastText,
                      fontSize: theme.typography.caption.fontSize,
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Grid>
          )}

          {/* License Document */}
          {application.piLicenseInfo?.licenseDocument && (
            <Grid item xs={12}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(1),
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body2.fontSize,
                }}
              >
                License Document
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing(2),
                  padding: theme.spacing(2),
                  backgroundColor: theme.customColors.searchBackground,
                  border: `1px solid ${theme.customColors.tableBorder}`,
                  borderRadius: theme.shapes.borderRadius.md,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      marginBottom: theme.spacing(0.5)
                    }}
                  >
                    {application.piLicenseInfo.licenseDocument.name || 'PI License Document'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: theme.spacing(1) }}>
                  <Tooltip title="View Document">
                    <IconButton
                      size="small"
                      onClick={handleViewDocument}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download Document">
                    <IconButton
                      size="small"
                      onClick={handleDownloadDocument}
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>


      </Box>

    </>
  );
};
